/**
 * Recently Viewed Products
 *
 * A dependency-free custom element for the PDP. On each product page it:
 *   1. Records the current product handle into localStorage (most-recent-first).
 *   2. Renders previously-viewed products as a horizontal, THE ROW-style row,
 *      hydrating each from Shopify's /products/{handle}.js endpoint.
 *
 * No jQuery, no cookies, no compiled-bundle dependency.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'je:recently-viewed';
  var MAX_STORED = 10; // how many handles we keep in history

  /**
   * Safe localStorage read. Returns an array of product handles (strings),
   * most-recent-first. Returns [] if storage is unavailable or empty.
   */
  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Safe localStorage write. Silently no-ops if storage is unavailable
   * (e.g. Safari private mode).
   */
  function writeStored(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {
      /* storage unavailable — feature degrades to no-op */
    }
  }

  /**
   * TODO(learning): Pure function — compute the new history array.
   *
   * Given the current product's handle and the existing stored array (most-
   * recent-first), return the NEW array to persist. This single function
   * defines the core behavior of the feature, so the rules matter:
   *
   *   - The current product should end up at the FRONT (most recent).
   *   - No duplicates: if currentHandle is already in `stored`, it must move
   *     to the front rather than appear twice.
   *   - Cap the result at MAX_STORED entries (drop the oldest).
   *   - Treat a blank/undefined currentHandle defensively (return stored as-is).
   *
   * @param {string} currentHandle - handle of the product being viewed now
   * @param {string[]} stored - existing handles, most-recent-first
   * @returns {string[]} new handles array to persist
   */
  function record(currentHandle, stored) {
    if (!currentHandle) return stored;
    // Drop any existing occurrence, put current at the front, then cap.
    var deduped = stored.filter(function (h) { return h !== currentHandle; });
    deduped.unshift(currentHandle);
    return deduped.slice(0, MAX_STORED);
  }

  /**
   * Fetch a single product's data from Shopify's JS endpoint.
   * Resolves to the product object, or null on failure (deleted/unpublished).
   */
  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('not ok');
        return res.json();
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * Format a price to render EXACTLY like the PDP's `{{ product.price | money }}`.
   *
   * This is a self-contained port of Shopify's money formatter, driven by the
   * shop's active money_format string (injected via data-money-format). It does
   * NOT depend on window.Shopify.formatMoney being loaded/timed, and it never
   * hardcodes a currency symbol — the format string alone decides the currency
   * (e.g. "{{amount_no_decimals}} USD" -> "88 USD"). This guarantees the recent
   * cards match the PDP even if the Shopify JS hasn't initialised yet.
   *
   * @param {number} cents - price in minor units from /products/{handle}.js
   * @param {string} format - the shop money_format string (Markets-resolved)
   * @returns {string} localized money string, e.g. "88 USD" or "$88.00"
   */
  function formatMoney(cents, format) {
    format = format || '';

    var currency =
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active;

    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) return '0';

      var value = (number / 100.0).toFixed(precision);
      var parts = value.split('.');

      var dollars = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands
      );

      var cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    }

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);

    if (!match) return format;

    var value;

    switch (match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2);
        break;

      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;

      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;

      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;

      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;

      default:
        value = withDelimiters(cents, 2);
    }


      var result = format.replace(placeholder, value);

      var currency =
        window.Shopify &&
        window.Shopify.currency &&
        window.Shopify.currency.active;

      var symbols = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        INR: '₹',
        CAD: '$',
        AUD: '$',
        JPY: '¥'
      };

      var symbol = symbols[currency] || '';

      if (symbol) {
        // Remove any existing currency symbol/prefix.
        result = result.replace(/^(C\$|A\$|CA\$|\$|€|£|₹|¥|Rs\.)\s*/, '');

        // Add the desired symbol.
        result = symbol + result;
      }

      // Make sure currency code remains at the end.
      if (
        currency &&
        !new RegExp('\\s' + currency + '$').test(result)
      ) {
        result += ' ' + currency;
      }

      return result;
  }
  
  function normalizeCurrencyDisplay(value, currency) {
    if (!value) return '';

    // Remove any existing currency symbol/prefix.
    value = value.replace(
      /^(C\$|CA\$|A\$|\$|€|£|₹|¥)\s*/,
      ''
    );

    // Canada/Australia/etc. should use $ as the prefix,
    // while keeping CAD/AUD at the end.
    if (currency === 'USD' ||
        currency === 'CAD' ||
        currency === 'AUD') {
      return '$' + value;
    }

    // India already has its own localized "Rs." prefix.
    if (currency === 'INR') {
      return 'Rs.' + value.replace(/^Rs\.\s*/i, '');
    }

    if (currency === 'EUR') {
      return '€' + value;
    }

    if (currency === 'GBP') {
      return '£' + value;
    }

    if (currency === 'JPY') {
      return '¥' + value;
    }

    return value;
  }
  customElements.define(
    'recently-viewed-products',
    class extends HTMLElement {
      connectedCallback() {
        this.currentHandle = this.getAttribute('data-handle') || '';
        this.limit = parseInt(this.getAttribute('data-limit'), 10) || 4;
        // Shop money format (with currency code) so prices match the PLP.
        this.moneyFormat = this.getAttribute('data-money-format') || '';

        this.track = this.querySelector('.rvp__track');
        this.template = this.querySelector('.rvp__card-template');
        this.prevArrow = this.querySelector('.rvp__arrow--prev');
        this.nextArrow = this.querySelector('.rvp__arrow--next');

        // 1. Record this visit.
        var stored = readStored();
        var updated = record(this.currentHandle, stored);
        writeStored(updated);

        // 2. Render everything EXCEPT the current product.
        var current = this.currentHandle;
        var toShow = updated
          .filter(function (h) { return h && h !== current; })
          .slice(0, this.limit);

        if (!toShow.length || !this.track || !this.template) {
          return; // nothing to show — section stays hidden via CSS :empty rule
        }

        this.renderCards(toShow);
      }

      renderCards(handles) {
        var self = this;
        Promise.all(handles.map(fetchProduct)).then(function (products) {
          var stored = readStored();
          var pruned = false;

          products.forEach(function (product, i) {
            if (!product) {
              // Prune dead handle from history.
              var dead = handles[i];
              var idx = stored.indexOf(dead);
              if (idx !== -1) { stored.splice(idx, 1); pruned = true; }
              return;
            }
            self.track.appendChild(self.buildCard(product));
          });

          if (pruned) writeStored(stored);

          var count = self.track.children.length;
          if (count) {
            // Match the collection grid: center the row when only 1–2 items,
            // otherwise left-align (default) so many items scroll naturally.
            self.classList.toggle('rvp--few', count <= 2);
            self.classList.add('rvp--ready');
            self.setupArrows();
          }
        });
      }

      /**
       * Wire up the mobile carousel arrows: tap to scroll one card, and
       * toggle prev/next visibility at the ends of the scroll range.
       * The .rvp--scrollable class (added only when the track overflows)
       * gates the arrows so they never show on the desktop/tablet grid.
       */
      setupArrows() {
        var self = this;
        var track = this.track;
        if (!this.prevArrow || !this.nextArrow) return;

        function cardStep() {
          var card = track.querySelector('.rvp__card');
          if (!card) return track.clientWidth;
          var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          return card.offsetWidth + gap;
        }

        function update() {
          // Only a carousel (overflowing track) gets arrows.
          var overflowing = track.scrollWidth - track.clientWidth > 1;
          self.classList.toggle('rvp--scrollable', overflowing);
          if (!overflowing) return;
          var max = track.scrollWidth - track.clientWidth;
          self.prevArrow.hidden = track.scrollLeft <= 1;
          self.nextArrow.hidden = track.scrollLeft >= max - 1;
        }

        this.prevArrow.addEventListener('click', function () {
          track.scrollBy({ left: -cardStep(), behavior: 'smooth' });
        });
        this.nextArrow.addEventListener('click', function () {
          track.scrollBy({ left: cardStep(), behavior: 'smooth' });
        });
        track.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
      }

      buildCard(product) {
        var node = this.template.content.firstElementChild.cloneNode(true);
        var links = node.querySelectorAll('.rvp__link');
        var img = node.querySelector('.rvp__image');
        var title = node.querySelector('.product-item__title');
        var color = node.querySelector('.product-item__color');
        var price = node.querySelector('.rvp__price');
        var comparePrice = node.querySelector('.rvp__compare-price');

        var url = product.url || ('/products/' + product.handle);
        for (var i = 0; i < links.length; i++) { links[i].href = url; }

        if (img) {
          var src = product.featured_image || (product.images && product.images[0]);
          if (src) {
            img.src = src.indexOf('http') === 0 ? src : 'https:' + src;
            img.alt = product.title || '';
          }
        }

        // Match collection tile: name before "/", color after "/".
        var parts = (product.title || '').split('/');
        if (title) title.textContent = (parts[0] || '').trim();
        if (color) color.textContent = (parts[1] || '').trim();


        if (price) {
          var hasCompare =
            product.compare_at_price &&
            product.compare_at_price > product.price;

          var currencyCode =
            (window.Shopify &&
              window.Shopify.currency &&
              window.Shopify.currency.active) || '';

          var formattedPrice = formatMoney(
            product.price,
            this.moneyFormat
          );

          formattedPrice = normalizeCurrencyDisplay(
            formattedPrice,
            currencyCode
          );

          price.textContent = formattedPrice;

          if (hasCompare) {
            price.classList.add('price-is-compare');
            price.classList.remove('price-regular');
          } else {
            price.classList.add('price-regular');
            price.classList.remove('price-is-compare');
          }
        }

        if (comparePrice) {
          if (
            product.compare_at_price &&
            product.compare_at_price > product.price
          ) {
            var formattedComparePrice = formatMoney(
              product.compare_at_price,
              this.moneyFormat
            );

            formattedComparePrice = normalizeCurrencyDisplay(
              formattedComparePrice,
              currencyCode
            );

            comparePrice.textContent = formattedComparePrice;
            comparePrice.style.display = '';
          } else {
            comparePrice.textContent = '';
            comparePrice.style.display = 'none';
          }
        }

        return node;
      }
    }
  );
})();
